// Port of the C++ report-dialog globals (TigerMainX.cpp / report_*_shell.cpp):
// g_report_start_time / g_report_end_time / g_report_turbine_state /
// g_report_month_date and report_time_shell's my_entered_times_srt_list.
// Values persist across dialog opens within a session so re-running a report
// repeats the previous selection.

export interface ReportTimeRange {
  startDate: string; // "YYYY-MM-DD"
  startTime: string; // "HH:MM:SS"
  endDate: string;
  endTime: string;
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

let lastRange: ReportTimeRange | null = null;
let lastTurbineState = "";
// g_report_month_date — shared by the month and year dialogs. month is 1-12.
let lastMonth: { month: number; year: number } | null = null;
// my_entered_times_srt_list — ranges previously entered in the TIME model
// report dialog, offered back as the pick list.
let enteredTimes: ReportTimeRange[] = [];

/** Last-used range; defaults to today 00:00:00 – 23:59:59 like the C++. */
export function getReportRange(): ReportTimeRange {
  if (lastRange) return { ...lastRange };
  const today = todayIso();
  return { startDate: today, startTime: "00:00:00", endDate: today, endTime: "23:59:59" };
}

export function setReportRange(range: ReportTimeRange): void {
  lastRange = { ...range };
}

export function getReportTurbineState(): string {
  return lastTurbineState;
}

export function setReportTurbineState(state: string): void {
  lastTurbineState = state;
}

/** Last-used month/year; defaults to the current month. */
export function getReportMonth(): { month: number; year: number } {
  if (lastMonth) return { ...lastMonth };
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

export function setReportMonth(month: number, year: number): void {
  lastMonth = { month, year };
}

export function getEnteredReportTimes(): ReportTimeRange[] {
  return enteredTimes.map((t) => ({ ...t }));
}

/** Record a TIME-model range, deduplicated, sorted by start (C++
 *  add_times_to_entered_time_list). */
export function addEnteredReportTime(range: ReportTimeRange): void {
  const key = (t: ReportTimeRange) => `${t.startDate}T${t.startTime}|${t.endDate}T${t.endTime}`;
  if (enteredTimes.some((t) => key(t) === key(range))) return;
  enteredTimes.push({ ...range });
  enteredTimes.sort((a, b) =>
    `${a.startDate}T${a.startTime}`.localeCompare(`${b.startDate}T${b.startTime}`),
  );
}

/** Test-only: clear all persisted report dialog state. */
export function __resetReportDialogStateForTest(): void {
  lastRange = null;
  lastTurbineState = "";
  lastMonth = null;
  enteredTimes = [];
}
